import React, { FC, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { LIMIT_PARAM_KEYWORD, TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { FilterOption } from 'models/Filters';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { LISTING_STAGE, LISTING_TYPE } from 'models/Listing';
import {
  DEAL_STATUS_FILTER_KEY,
  DEAL_FROM_FILTER_KEY,
  DEAL_TO_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
  LISTING_TYPE_FILTER_KEY,
  VIEW_AS_NAME,
  VIEW_AS_USER,
} from './data';
import { PER_PAGE } from 'components/NumberOfResultsPerPageFilter/NumberOfResultsPerPageFilter.component';
import { DEAL_STATUS_LABEL, LISTING_STATUS_LABEL } from 'data/dealStatus';
import { LOCATION_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { DEAL_STAGE } from 'models/DealRoom';
import { INDUSTRY_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import MultiFilter from 'components/MultiFilter';
import api from 'api';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import { TIMEFRAME } from 'dateUtils';

import './PortfolioSellerFilters.styles.scss';

type PortfolioSellerFiltersProps = {
  className?: string;
};

const PortfolioSellerFilters: FC<PortfolioSellerFiltersProps> = ({
  className,
}) => {
  const classes = classNames('portfolio-seller-filters', className);
  const {
    isSuperAdmin,
    isEnterpriseHead,
    isOfficeManager,
    currentUser: { hasSellingUrgentDeals, isGridViewForDeals },
  } = useContext(CurrentUserContext);

  const createSelectOptions = useCreateSelectOptions();

  const { locationOptions, industrySelectOptions } =
    useOperationStepValues(true);

  const { getSearchableFilter, getPriceRangeFilter } = useGetFilterOptions();

  const isTableView = useMemo(() => !isGridViewForDeals, [isGridViewForDeals]);

  const getViewAsOptions = useCallback(async (searchString: string) => {
    const { data } = await api.portfolio.admin.getBrokers({
      $searchString: searchString,
    });

    return data.map(({ userId, name }) => ({
      value: userId,
      label: name,
    }));
  }, []);

  const filterOptions = useMemo(
    () =>
      [
        {
          value: LOCATION_FILTER_KEY,
          label: t('location'),
          isMultiple: true,
          options: locationOptions,
        },
        {
          value: INDUSTRY_FILTER_KEY,
          label: t('industry'),
          isMultiple: true,
          isSearchable: true,
          options: industrySelectOptions,
        },
        getPriceRangeFilter(t('ev'), DEAL_FROM_FILTER_KEY, DEAL_TO_FILTER_KEY),
        ...(isSuperAdmin || isEnterpriseHead || isOfficeManager
          ? [
              getSearchableFilter(
                t('viewAs', { name: '' }),
                VIEW_AS_USER,
                VIEW_AS_NAME,
                getViewAsOptions,
                false,
                'viewAs',
              ),
            ]
          : []),
        {
          value: DEAL_STATUS_FILTER_KEY,
          label: t('stage'),
          options: Object.values(DEAL_STAGE).map((value) => ({
            value,
            label: t(DEAL_STATUS_LABEL[value]),
          })),
        },
        {
          value: LISTING_STATUS_FILTER_KEY,
          label: t('listing'),
          options: Object.values(LISTING_STAGE).map((value) => ({
            value,
            label: t(LISTING_STATUS_LABEL[value]),
          })),
        },
        ...(hasSellingUrgentDeals
          ? [
              {
                value: LISTING_TYPE_FILTER_KEY,
                label: t('dealType'),
                options: Object.values(LISTING_TYPE).map((value) => ({
                  value,
                  label: t(value),
                })),
              },
            ]
          : []),
        {
          value: TIMEFRAME_PARAM_KEYWORD,
          label: t('createdDuring'),
          shouldSortOptions: false,
          options: createSelectOptions(Object.values(TIMEFRAME)),
        },
        ...(isTableView
          ? [
              {
                value: LIMIT_PARAM_KEYWORD,
                label: t('limit'),
                shouldSortOptions: false,
                options: createSelectOptions(PER_PAGE),
              },
            ]
          : []),
      ] as FilterOption[],
    [
      locationOptions,
      industrySelectOptions,
      isSuperAdmin,
      isEnterpriseHead,
      hasSellingUrgentDeals,
      isTableView,
      isOfficeManager,
      getPriceRangeFilter,
      getSearchableFilter,
      getViewAsOptions,
      createSelectOptions,
    ],
  );

  return <MultiFilter className={classes} filterOptions={filterOptions} />;
};

export default PortfolioSellerFilters;
